import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, type HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { Storage } from '@ionic/storage-angular';
import { ToastController } from '@ionic/angular';
import { Router } from '@angular/router';
import { ConfigService } from '../services/config/config.service';
import { ExportAsExcelService } from '../services/export-as-excel.service';
import { AuthenticationService } from '../services/authentication/authentication.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  // Prevents a burst of failed requests from each firing their own "session expired"
  // toast / logout. Reset shortly after so the next real session can expire again.
  private sessionExpiredHandled = false;

  constructor(
    private storage: Storage,
    private toastController: ToastController,
    private configService: ConfigService,
    private router: Router,
    private exportAsExcelService: ExportAsExcelService,
    // Injected lazily (via Injector) to avoid the circular DI:
    // HttpClient -> HTTP_INTERCEPTORS -> AuthenticationService -> HttpClient.
    private injector: Injector,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return from(this.storage.get('auth-token')).pipe(
      switchMap((token) => {
        let headers = request.headers;

        if (token) {
          headers = headers.set('Authorization', token);
        }

        if (!(request.body instanceof FormData)) {
          if (!headers.has('Content-Type')) {
            headers = headers.set('Content-Type', 'application/json');
          }
          if (!headers.has('Accept')) {
            headers = headers.set('Accept', 'application/json');
          }
        }

        const authReq = request.clone({ headers });
        return next.handle(authReq);
      }),
      catchError((error: HttpErrorResponse) => {
        this.handleError(error, request);
        return throwError(error);
      }),
    );
  }

  /**
   * An expired or invalid token surfaces either as HTTP 401 or as an OAuth2
   * resource-server body of `{ error: 'invalid_token', ... }`. Either way we
   * treat it as a dead session.
   */
  private isAuthError(error: HttpErrorResponse): boolean {
    if (error.status === 401 || error.status === 403) {
      return true;
    }
    const body: any = error.error;
    if (body && typeof body === 'object') {
      const code = typeof body.error === 'string' ? body.error : body.error?.error;
      if (typeof code === 'string' && /invalid_token|invalid_grant|access token expired/i.test(code)) {
        return true;
      }
    }
    return false;
  }

  /**
   * A gateway/resource-server 401 served without CORS headers reaches the browser
   * as status 0 with no body, so it slips past isAuthError(). Treat status 0 as a
   * dead session ONLY when we actually sent a token — otherwise a transient
   * network blip would log the user out.
   */
  private async isMaskedAuthError(error: HttpErrorResponse): Promise<boolean> {
    if (error.status !== 0) {
      return false;
    }
    try {
      const token = await this.storage.get('auth-token');
      return !!token;
    } catch {
      return false;
    }
  }

  /**
   * The emailed reset link lands the (logged-out) user on /changePassword, which
   * calls an authenticated endpoint with the one-time URL token. These public pages
   * must own their own auth failures rather than being redirected to /login.
   */
  private isOnPublicResetRoute(): boolean {
    const url = this.router.url || '';
    return url.startsWith('/changePassword') || url.startsWith('/forgotPassword');
  }

  /**
   * Login / forgot-password / reset-password are *unauthenticated* requests — the
   * user has no session yet. A 401 here means bad credentials (or an invalid reset
   * link), NOT an expired session, so it must never trigger the "session expired"
   * toast + logout redirect. The calling page owns and displays these failures.
   */
  private isUnauthenticatedRequest(request: HttpRequest<unknown>): boolean {
    const url = request?.url || '';
    return (
      url.includes('/api/user/login') ||
      url.includes('/api/user/forgotPassword') ||
      url.includes('/api/user/resetPassword')
    );
  }

  private async handleError(error: HttpErrorResponse, request: HttpRequest<unknown>) {
    // Failures on unauthenticated auth endpoints (login/forgot/reset) belong to the
    // page that issued them — let the error propagate untouched so it can show the
    // real message (e.g. "Invalid username/password") instead of "session expired".
    if (this.isUnauthenticatedRequest(request)) {
      return;
    }

    // A burst of concurrent requests (e.g. an action's ngOnInit) all fail together
    // when the session dies. Once teardown has started, swallow the follow-on
    // failures instead of toasting "unexpected error" for each one.
    if (this.sessionExpiredHandled) {
      return;
    }

    // Token expired / invalid: clear the session cleanly and send the user to login,
    // instead of showing a confusing generic error and leaving the page stuck.
    if (this.isAuthError(error) || (await this.isMaskedAuthError(error))) {
      // The public password-reset pages legitimately call an authenticated endpoint
      // (findById) with a one-time reset token while the user is logged out. A 401
      // there must not hijack them to /login with a misleading "session expired" —
      // let the page handle it so they can still set a new password.
      if (this.isOnPublicResetRoute()) {
        return;
      }
      await this.handleSessionExpired();
      return;
    }

    // Re-check: while we awaited isMaskedAuthError() above, a concurrent request may
    // have started session teardown (and cleared the token, which is exactly why our
    // own isMaskedAuthError() just returned false). Don't show a generic error on top.
    if (this.sessionExpiredHandled) {
      return;
    }

    // Any errored request must clear a stray global loader, otherwise a component
    // that showed a loader and skipped its dismiss() on throw leaves the page stuck.
    try {
      await this.configService.dismiss();
    } catch (e) {
      console.log(e);
    }

    let message = 'An unexpected error occurred';

    if (error.error) {
      if (typeof error.error === 'string') {
        message = error.error;
      } else if (error.error.message) {
        message = error.error.message;
      } else if (typeof error.error.error === 'string') {
        message = error.error.error;
      } else if (error.error.error && error.error.error.message) {
        message = error.error.error.message;
      }
    } else if (error.message) {
      message = error.message;
    }

    if (error.error && error.error.data && Array.isArray(error.error.data) && error.error.data.length > 0) {
      let excelData: any[] = [];
      error.error.data.forEach((el: any) => {
        if (el.StoneId && Array.isArray(el.StoneId)) {
          el.StoneId.map((item: any) => excelData.push({ Name: item.StoneId || item, message: el.message }))
        } else if (el.StoneId) {
          excelData.push({ Name: el.StoneId, message: el.message });
        } else {
          excelData.push(el);
        }
      });
      if (excelData.length > 0) {
        this.exportAsExcelService.exportAsExcelFile(excelData, 'Error_Stones');
      }
    }

    this.configService.showToast('error', message);
  }

  private async handleSessionExpired() {
    if (this.sessionExpiredHandled) {
      return;
    }
    this.sessionExpiredHandled = true;
    // Allow a fresh session to expire and be reported again later.
    setTimeout(() => (this.sessionExpiredHandled = false), 5000);

    // Make sure no "Please Wait.." loader is left spinning over the login redirect.
    try {
      await this.configService.dismiss();
    } catch (e) {
      console.log(e);
    }

    this.configService.showToast('error', 'Your session has expired. Please login again.');

    // Full cleanup: clears storage, unsubscribes SSE, flips auth state, redirects to /login.
    // Falls back to a manual clear+redirect if the service can't be resolved.
    try {
      const authService = this.injector.get(AuthenticationService);
      await authService.logout();
    } catch (e) {
      console.log(e);
      await this.storage.remove('auth-token');
      this.router.navigate(['/login']);
    }
  }
}
